import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  ChevronRight,
  MapPin,
  Mail,
  Phone,
  Star,
  CheckCircle2,
  Quote,
  Sparkles,
  Users,
  Calendar,
  Instagram,
  Facebook,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import SEO from '../components/common/SEO';

// Logo
const UPA_LOGO =
  'https://customer-assets.emergentagent.com/job_4cf29391-a054-46ca-8c58-6d2c15185384/artifacts/bn56j1c1_gradient%20print.png';

// Celebrity testimonials (re-used across the site)
const celebTestimonials = [
  {
    quote: 'This school has it all, learn to perform from industry professionals.',
    author: 'Suzanne Shaw',
    image:
      'https://customer-assets.emergentagent.com/job_class-manager-84/artifacts/3io8fwpk_Suzanne_Shaw.webp',
  },
  {
    quote:
      'If you want to meet like minded people and become a successful actor then UPA is for you.',
    author: 'Jason Manford',
    image:
      'https://customer-assets.emergentagent.com/job_class-manager-84/artifacts/kxsqh3xy_Jason-Manford-_Olivier_Awards_2019_Pamela_Raith_Photography1_Standard.jpg',
  },
  {
    quote:
      'If you want to make it on Broadway or in the West End you have to train and UPA is where you want to go!',
    author: 'Cory English',
    role: 'Broadway and West End Star',
    image:
      'https://customer-assets.emergentagent.com/job_class-manager-84/artifacts/2d46keau_news-body-image_38172.jpg',
  },
];

// Parent testimonials — repeat across SEO pages for trust signals
const parentTestimonials = [
  {
    quote:
      'UPA is the best theatre school in Milton Keynes. My daughter has grown in confidence and loves every class.',
    author: 'Sarah (UPA parent)',
  },
  {
    quote:
      'The teachers are inspiring, supportive and properly professional. Worth every penny.',
    author: 'Michael (UPA parent)',
  },
  {
    quote:
      'The end-of-term productions are outstanding and every child gets a chance to shine on stage.',
    author: 'Helen (UPA parent)',
  },
];

// Shared FAQ block — used here and on the homepage
export const sharedFaqs = [
  {
    q: 'What age groups do you teach?',
    a: 'We offer performing arts classes in Milton Keynes for children and teenagers aged 4 to 18, Primatotterinas for ages 3–5, and adult classes too — including Adult Street Dance for grown-ups who want to perform, get fit and have fun.',
  },
  {
    q: 'Do students need previous experience?',
    a: 'No. Beginners are always welcome. Every class is tailored so confident performers and complete newcomers can thrive side by side.',
  },
  {
    q: 'What standard of training will my child get?',
    a: 'The very best. UPA only brings in industry professionals — West End performers, working actors, TV credits, professional choreographers and vocal coaches — so your child trains the way real performers train. We don\'t compromise on the team in front of the studio, ever.',
  },
  {
    q: 'Where are the classes held?',
    a: 'Saturday classes run at Kents Hill Secondary School in Milton Keynes (MK7 6BZ). Weekday classes run at Watling Academy, Whitehouse, Milton Keynes (MK8 1EP).',
  },
  {
    q: 'Can my child try a class before signing up?',
    a: 'Yes — every new student is welcome to a free trial class subject to availability. Book online via our Free Trial page.',
  },
  {
    q: 'Is there a sibling discount?',
    a: 'Yes. We offer 30% off for every additional sibling, applied automatically when you book online.',
  },
];

const SeoLandingPage = ({ config }) => {
  const navigate = useNavigate();

  const {
    seoKey,
    eyebrow,
    h1,
    h1Accent,
    intro,
    heroImage,
    bullets = [],
    whatYouLearn = [],
    ageGroups = [],
    relatedLinks = [],
    primaryCta = { label: 'BOOK FREE TRIAL', to: '/free-trial' },
    secondaryCta = { label: 'VIEW CLASSES', to: '/classes' },
  } = config;

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid={`seo-page-${seoKey}`}>
      <SEO page={seoKey} />
      <Navbar />

      {/* HERO */}
      <section className="relative pt-20 pb-12 sm:pt-24 sm:pb-14 overflow-hidden bg-[#0a0a0a]">
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt={h1}
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#7000FF]/20 via-transparent to-[#FF007F]/20" />
        </div>

        <div className="relative max-w-5xl mx-auto px-6 text-center">
          <span className="font-accent text-base sm:text-lg tracking-[0.3em] text-[#FF007F] uppercase">
            {eyebrow}
          </span>
          <h1
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold uppercase mt-4 mb-6 leading-tight"
            data-testid={`seo-h1-${seoKey}`}
          >
            {h1}{' '}
            {h1Accent && (
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF007F] to-[#7000FF]">
                {h1Accent}
              </span>
            )}
          </h1>
          <p className="text-lg sm:text-xl text-white/80 max-w-3xl mx-auto leading-relaxed">
            {intro}
          </p>

          <div className="flex items-center justify-center gap-2 mt-6 text-white/60 text-sm">
            <MapPin className="w-4 h-4 text-[#FF007F]" />
            <span>Milton Keynes · Kents Hill (MK7 6BZ) · Whitehouse (MK8 1EP)</span>
          </div>

          <div className="flex flex-wrap justify-center gap-4 pt-8">
            <Button
              size="lg"
              className="bg-[#FF007F] hover:bg-[#D6006A] text-white font-bold uppercase tracking-wider px-10 py-6 rounded-none"
              onClick={() => navigate(primaryCta.to)}
              data-testid={`seo-primary-cta-${seoKey}`}
            >
              {primaryCta.label}
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-black font-bold uppercase tracking-wider px-10 py-6 rounded-none"
              onClick={() => navigate(secondaryCta.to)}
              data-testid={`seo-secondary-cta-${seoKey}`}
            >
              {secondaryCta.label}
            </Button>
          </div>
        </div>
      </section>

      {/* INTRO + BULLETS */}
      {bullets.length > 0 && (
        <section className="pt-10 pb-20 px-6 lg:px-12 bg-[#050505]">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <span className="font-accent text-base tracking-[0.3em] text-[#7000FF] uppercase">
                Why UPA
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold uppercase mt-4 mb-6">
                Built for performers in{' '}
                <span className="text-[#FF007F]">Milton Keynes</span>
              </h2>
              <p className="text-white/70 leading-relaxed">
                Universal Performing Arts is the Milton Keynes theatre school
                trusted by hundreds of local families. Our weekly classes blend
                acting, singing and dance into one focused triple-threat
                training session — so your child develops every skill they need
                to thrive on stage.
              </p>
            </div>
            <ul className="space-y-4">
              {bullets.map((b, i) => (
                <li key={i} className="flex gap-3 items-start">
                  <CheckCircle2 className="w-6 h-6 text-[#FF007F] flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-heading text-lg uppercase font-bold mb-1">
                      {b.title}
                    </h3>
                    <p className="text-white/65 text-sm leading-relaxed">{b.body}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* WHAT YOU LEARN */}
      {whatYouLearn.length > 0 && (
        <section className="py-20 px-6 lg:px-12 bg-[#0a0a0a]">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <span className="font-accent text-base tracking-[0.3em] text-[#7000FF] uppercase">
                Inside the studio
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold uppercase mt-4">
                What your child will{' '}
                <span className="text-[#FF007F]">learn</span>
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {whatYouLearn.map((item, i) => (
                <div
                  key={i}
                  className="bg-[#111] border border-[#222] p-6 hover:border-[#FF007F]/50 transition-colors"
                >
                  <Sparkles className="w-7 h-7 text-[#FF007F] mb-4" />
                  <h3 className="font-heading text-lg uppercase font-bold mb-2">
                    {item.title}
                  </h3>
                  <p className="text-white/65 text-sm">{item.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* AGE GROUPS */}
      {ageGroups.length > 0 && (
        <section className="py-20 px-6 lg:px-12 bg-[#050505]">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <span className="font-accent text-base tracking-[0.3em] text-[#7000FF] uppercase">
                Classes by age
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold uppercase mt-4">
                Find your child's <span className="text-[#FF007F]">group</span>
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {ageGroups.map((g, i) => (
                <div
                  key={i}
                  className="bg-[#111] border border-[#222] p-5 text-center hover:border-[#7000FF]/50 transition-colors"
                >
                  <Users className="w-6 h-6 text-[#7000FF] mx-auto mb-3" />
                  <div className="font-heading text-2xl uppercase font-bold text-[#FFD700]">
                    {g.ageRange}
                  </div>
                  <div className="text-sm text-white/70 mt-1 uppercase tracking-wider">
                    {g.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* TESTIMONIALS */}
      <section className="py-20 px-6 lg:px-12 bg-[#0a0a0a]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="font-accent text-base tracking-[0.3em] text-[#7000FF] uppercase">
              Love from local families
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold uppercase mt-4">
              What <span className="text-[#FF007F]">parents</span> say
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {parentTestimonials.map((t, i) => (
              <div
                key={i}
                className="bg-[#111] border border-[#222] p-6 hover:border-[#FF007F]/50 transition-colors"
              >
                <Quote className="w-6 h-6 text-[#FF007F] mb-3" />
                <blockquote className="text-white/75 text-sm italic leading-relaxed mb-4">
                  "{t.quote}"
                </blockquote>
                <div className="flex items-center gap-2">
                  <div className="flex">
                    {[...Array(5)].map((_, s) => (
                      <Star
                        key={s}
                        className="w-3.5 h-3.5 fill-[#FFD700] text-[#FFD700]"
                      />
                    ))}
                  </div>
                  <span className="text-white/60 text-xs">— {t.author}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Celebrity row — compact */}
          <div className="grid md:grid-cols-3 gap-6">
            {celebTestimonials.map((c, i) => (
              <div
                key={i}
                className="flex items-center gap-4 bg-[#0a0a0a] border border-[#222] p-4"
              >
                <img
                  src={c.image}
                  alt={c.author}
                  loading="lazy"
                  className="w-14 h-14 rounded-full object-cover grayscale"
                />
                <div>
                  <p className="text-xs text-white/70 italic">"{c.quote}"</p>
                  <p className="font-heading text-sm uppercase mt-1">{c.author}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-6 lg:px-12 bg-[#050505]">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <span className="font-accent text-base tracking-[0.3em] text-[#7000FF] uppercase">
              Questions
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold uppercase mt-4">
              Frequently <span className="text-[#FF007F]">asked</span>
            </h2>
          </div>
          <Accordion type="single" collapsible className="w-full" data-testid={`seo-faq-${seoKey}`}>
            {sharedFaqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-[#222]">
                <AccordionTrigger
                  className="text-left text-white hover:text-[#FF007F] font-heading uppercase text-base"
                  data-testid={`faq-trigger-${seoKey}-${i}`}
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-white/70">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* RELATED LINKS */}
      {relatedLinks.length > 0 && (
        <section className="py-16 px-6 lg:px-12 bg-[#0a0a0a]">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-heading text-2xl uppercase font-bold text-center mb-8">
              Explore more <span className="text-[#FF007F]">classes</span> at UPA
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {relatedLinks.map((r, i) => (
                <Link
                  key={i}
                  to={r.to}
                  className="group flex items-center justify-between bg-[#111] border border-[#222] p-5 hover:border-[#FF007F]/50 transition-colors"
                  data-testid={`seo-related-${seoKey}-${i}`}
                >
                  <span className="font-heading uppercase text-sm tracking-wider text-white group-hover:text-[#FF007F] transition-colors">
                    {r.label}
                  </span>
                  <ChevronRight className="w-4 h-4 text-[#FF007F]" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="py-20 px-6 lg:px-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#7000FF] to-[#FF007F]" />
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold uppercase mb-4">
            Book your free trial
          </h2>
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
            See for yourself why families across Milton Keynes choose UPA. No
            obligation — just a fun class.
          </p>
          <Button
            size="lg"
            className="bg-white text-[#FF007F] hover:bg-white/90 font-bold uppercase tracking-wider px-10 py-6 rounded-none"
            onClick={() => navigate('/free-trial')}
            data-testid={`seo-bottom-cta-${seoKey}`}
          >
            BOOK FREE TRIAL
          </Button>
        </div>
      </section>

      {/* FOOTER — slim version */}
      <footer className="py-12 px-6 lg:px-12 bg-[#050505] border-t border-[#222]">
        <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-8">
          <div>
            <img src={UPA_LOGO} alt="UPA" className="h-16 w-auto mb-3" />
            <p className="text-white/50 text-sm">
              Training the stars of tomorrow in Milton Keynes since 2014.
            </p>
          </div>
          <div>
            <h4 className="font-heading text-base uppercase font-bold mb-3">
              Find a Class
            </h4>
            <ul className="space-y-2 text-sm text-white/60">
              <li><Link to="/musical-theatre-classes-milton-keynes" className="hover:text-[#FF007F]">Musical Theatre Classes</Link></li>
              <li><Link to="/acting-classes-milton-keynes" className="hover:text-[#FF007F]">Acting Classes</Link></li>
              <li><Link to="/singing-classes-milton-keynes" className="hover:text-[#FF007F]">Singing Classes</Link></li>
              <li><Link to="/dance-classes-milton-keynes" className="hover:text-[#FF007F]">Dance Classes</Link></li>
              <li><Link to="/theatre-school-milton-keynes" className="hover:text-[#FF007F]">Theatre School Milton Keynes</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-heading text-base uppercase font-bold mb-3">Contact</h4>
            <a href="tel:+447310105678" className="text-white/60 text-sm flex items-center gap-2 hover:text-[#FF007F]">
              <Phone className="w-4 h-4" /> +44 7310 105678
            </a>
            <a href="mailto:theteam@upaschool.com" className="text-white/60 text-sm flex items-center gap-2 hover:text-[#FF007F] mt-2">
              <Mail className="w-4 h-4" /> theteam@upaschool.com
            </a>
            <p className="text-white/50 text-sm flex items-start gap-2 mt-3">
              <MapPin className="w-4 h-4 mt-0.5" />
              <span>Kents Hill Secondary School, Milton Keynes, MK7 6BZ</span>
            </p>
            <div className="flex items-center gap-3 mt-4">
              <a
                href="https://www.instagram.com/upa_school/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="UPA on Instagram"
                data-testid="footer-social-instagram"
                className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center hover:border-[#FF007F] hover:bg-white/5 transition-colors"
              >
                <Instagram className="w-4 h-4" style={{ stroke: 'url(#upaSocialGrad)' }} />
              </a>
              <a
                href="https://www.facebook.com/upaschool"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="UPA on Facebook"
                data-testid="footer-social-facebook"
                className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center hover:border-[#FF007F] hover:bg-white/5 transition-colors"
              >
                <Facebook className="w-4 h-4" style={{ stroke: 'url(#upaSocialGrad)' }} />
              </a>
            </div>
          </div>
        </div>
        <div className="border-t border-[#222] mt-8 pt-6 text-center text-white/30 text-sm">
          © {new Date().getFullYear()} Universal Performing Arts — Theatre school in Milton Keynes.
        </div>
      </footer>
    </div>
  );
};

export default SeoLandingPage;
